import React, { useState } from 'react'

import { ActiveSource, TWriteMode } from 'types/GlobalSource'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Text from 'components_new/atoms/Text'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

import FormSection from 'components_new/molecules/FormSection'
import FormToggle from 'components_new/molecules/FormToggle'
import UploadFile from 'components_new/molecules/UploadFile'

import SftpPanel from './SftpPanel'

import { useActiveSourcesActions } from 'redux/actions/DataPlatform/connections/ActiveSources'
import {
  useSourcesCreating,
  useSourcesCreatingSftp,
  useSourcesUpdating
} from 'redux/hooks/Sources'

const STRUCTURE_ERROR =
  'Strukturen på filen skiljer sig från den förra uppladdningen. ' +
  'Kolumnerna måste vara desamma för att datan ska kunna läsas in.'

interface SourceUploadProps {
  activeSource: ActiveSource
}

const SourceUpload = (props: SourceUploadProps) => {
  const { activeSource } = props

  const creating = useSourcesCreating()
  const creatingSftp = useSourcesCreatingSftp()
  const updating = useSourcesUpdating()
  const {
    tryCreateSFTPSettings,
    tryUpdateActiveSource,
    tryUploadActiveSourceFile
  } = useActiveSourcesActions()

  const [file, setFile] = useState<File | null>(null)
  const [uploadFailed, setUploadFailed] = useState(false)

  /**
   * Only the single-source endpoint returns the credentials, so this is empty
   * for the moment between the list landing and that call resolving.
   */
  const sftpSetting = (activeSource.included.sftp_settings ?? [])[0] ?? null

  const writeMode: TWriteMode = activeSource.attributes.write_mode ?? 'append'
  const multipleFiles = Boolean(activeSource.attributes.multiple_files)

  const handleChangeWriteMode = (overwrite: boolean) => {
    tryUpdateActiveSource(activeSource.id, {
      data: {
        attributes: { write_mode: overwrite ? 'overwrite' : 'append' }
      }
    })
  }

  const handleUpload = () => {
    if (!file) {
      return
    }

    const body = new FormData()

    body.append('file', file)
    // The backend persists the mode from here, so it always travels with the
    // file rather than relying on the source already being saved.
    body.append('writeMode', writeMode)

    setUploadFailed(false)

    tryUploadActiveSourceFile(activeSource.id, body, (err?: boolean) => {
      if (err) {
        setUploadFailed(true)
      } else {
        setFile(null)
      }
    })
  }

  return (
    <Box>
      <FormSection
        helperText="Bestämmer vad som händer med tidigare inläst data när en ny fil kommer in."
        title="Skrivläge"
      >
        <FormToggle
          body="Vid varje uppladdning raderas tidigare data och ersätts av den nya filen. Är den av läggs ny data till."
          icon={{ name: 'Sync' }}
          onChange={handleChangeWriteMode}
          title="Skriv över data"
          value={writeMode === 'overwrite'}
        />
      </FormSection>

      <FormSection
        helperText="Välj om filerna ska levereras till en SFTP-server eller laddas upp manuellt."
        title="Uppladdning"
      >
        <TooltipWrapper
          title={
            sftpSetting
              ? 'SFTP-uppgifterna kan inte tas bort härifrån.'
              : undefined
          }
        >
          <span>
            <FormToggle
              body="Leverera filer till en SFTP-server i stället för att ladda upp dem manuellt."
              disabled={Boolean(sftpSetting) || creatingSftp}
              icon={{ name: 'CloudUploadOutlined' }}
              onChange={() => tryCreateSFTPSettings(activeSource.id)}
              title="Uppladdning via SFTP"
              value={Boolean(sftpSetting)}
            />
          </span>
        </TooltipWrapper>

        <TooltipWrapper
          title={
            multipleFiles
              ? 'Stödet kan inte stängas av när det har aktiverats.'
              : undefined
          }
        >
          <span>
            <FormToggle
              body="Läs in flera filer till samma källa. Manuell uppladdning stängs av."
              disabled={multipleFiles || updating}
              icon={{ name: 'File' }}
              onChange={() =>
                tryUpdateActiveSource(activeSource.id, {
                  data: { attributes: { multiple_files: true } }
                })
              }
              title="Stöd för flertalet filer"
              value={multipleFiles}
            />
          </span>
        </TooltipWrapper>

        {sftpSetting ? <SftpPanel sftpSetting={sftpSetting} /> : null}

        {!sftpSetting && multipleFiles ? (
          <Text color="text.secondary" variant="body2">
            Filer kan inte laddas upp manuellt när stöd för flertalet filer är
            aktiverat.
          </Text>
        ) : null}

        {!sftpSetting && !multipleFiles ? (
          <Box>
            <UploadFile
              accept={['text/csv', 'text/plain', 'application/vnd.ms-excel']}
              uploadFile={(value) => {
                setUploadFailed(false)
                setFile(value)
              }}
            />

            {file ? (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'space-between',
                  mt: 2
                }}
              >
                <Text variant="body2">{file.name}</Text>
                <Button loading={creating} onClick={handleUpload}>
                  Ladda in data
                </Button>
              </Box>
            ) : null}

            {uploadFailed ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {STRUCTURE_ERROR}
              </Alert>
            ) : null}
          </Box>
        ) : null}
      </FormSection>
    </Box>
  )
}

export default SourceUpload
